import _extends from "@babel/runtime/helpers/esm/extends";
import React, { useState, useEffect, useReducer, useMemo, useRef } from "react";
import ClassNames from "classnames";
import DateSinglePicker from "./date-picker/DateSinglePicker";
import DateRangePicker from "./date-picker/DateRangePicker";
import CalendarPickerContext from "./context/CalendarPickerContext";
import { DateValidator, DateCalculator } from "./utils/dateUtils";
import momentGenerator from "./utils/momentGenerator";
import { viewReducerGen, activeStartDateReducerGen, keyboardControlAreaReducerGen, openStatusReducerGen } from './reducers';
import inputValueReducer from "./reducers/inputValueReducer";
import { useUpdateEffect, usePrevious } from '../utils';
import valueReducerGen from "./reducers/valueReducer";
import { DEFAULT_DATE_FORMAT, DetailToGranularityMap, FORMAT_GROUP, LOCALIZED_FORMAT } from './utils/constants';
import { _uuid } from '../utils/index';
var CalendarPicker = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var id = props.id,
    selectRange = props.selectRange,
    className = props.className,
    style = props.style,
    _props$format = props.format,
    formatProp = _props$format === void 0 ? DEFAULT_DATE_FORMAT : _props$format,
    _props$rangeConnecter = props.rangeConnecterFormat,
    rangeConnecterFormat = _props$rangeConnecter === void 0 ? 'to' : _props$rangeConnecter,
    onChange = props.onChange,
    _props$viewStart = props.viewStart,
    viewStart = _props$viewStart === void 0 ? 'month' : _props$viewStart,
    _props$viewEnd = props.viewEnd,
    viewEnd = _props$viewEnd === void 0 ? 'decade' : _props$viewEnd,
    disabled = props.disabled,
    mode = props.mode,
    value = props.value,
    defaultValue = props.defaultValue,
    minDate = props.minDate,
    maxDate = props.maxDate,
    disabledDays = props.disabledDays,
    disabledDates = props.disabledDates,
    disabledRanges = props.disabledRanges,
    onValueChange = props.onValueChange,
    _props$editable = props.editable,
    editable = _props$editable === void 0 ? true : _props$editable,
    _props$dateSeparator = props.dateSeparator,
    dateSeparator = _props$dateSeparator === void 0 ? '/' : _props$dateSeparator,
    _props$hideTitleOnInp = props.hideTitleOnInput,
    hideTitleOnInput = _props$hideTitleOnInp === void 0 ? false : _props$hideTitleOnInp,
    _props$datepickerAlwa = props.datepickerAlwaysDown,
    datepickerAlwaysDown = _props$datepickerAlwa === void 0 ? true : _props$datepickerAlwa,
    datePanelHorizontalAlign = props.datePanelHorizontalAlign,
    _props$error = props.error,
    error = _props$error === void 0 ? false : _props$error,
    useCustomValidation = props.useCustomValidation,
    customErrorNode = props.customErrorNode;
  var dateRef = React.useRef(null);
  var maxDetail = viewStart,
    minDetail = viewEnd;
  var editForbidden = useMemo(function () {
    var compositeKey = viewStart + "-" + viewEnd;
    var formatList = FORMAT_GROUP.get(compositeKey);
    return !(formatList && formatList.indexOf(LOCALIZED_FORMAT[formatProp] || formatProp) !== -1);
  }, [props.format, viewStart, viewEnd]);
  var dateUtils = useMemo(function () {
    var newFormat = LOCALIZED_FORMAT[formatProp] || formatProp;
    var granularity = maxDetail === 'year' ? 'month' : maxDetail === 'decade' ? 'year' : 'day';
    var res = {
      format: newFormat,
      dateValidator: new DateValidator(newFormat, momentGenerator, rangeConnecterFormat, granularity, maxDetail),
      dateCalculator: new DateCalculator(newFormat, momentGenerator, rangeConnecterFormat, granularity, maxDetail),
      momentUtils: momentGenerator(newFormat, granularity)
    };
    if (dateSeparator && dateSeparator.length > 1) {
      console.warn('Type Error: dateSeparator must be of length 1');
      return res;
    }
    if (dateSeparator && dateSeparator.length === 1 && !editForbidden) {
      newFormat = newFormat.replace(/\//g, dateSeparator);
      return {
        format: newFormat,
        dateValidator: new DateValidator(newFormat, momentGenerator, rangeConnecterFormat, granularity, maxDetail),
        dateCalculator: new DateCalculator(newFormat, momentGenerator, rangeConnecterFormat, granularity, maxDetail),
        momentUtils: momentGenerator(newFormat, granularity)
      };
    }
    return res;
  }, [dateSeparator, rangeConnecterFormat, maxDetail]);
  var _useMemo = useMemo(function () {
      var showPanel = mode === "showRangePanel" || mode === "showDoubleCalendarWithRangePanel";
      var isInDoubleCalendar = mode === "showDoubleCalendar" || mode === "showDoubleCalendarWithRangePanel";
      return {
        showPanel: showPanel,
        isInDoubleCalendar: isInDoubleCalendar
      };
    }, [mode]),
    isInDoubleCalendar = _useMemo.isInDoubleCalendar,
    showPanel = _useMemo.showPanel;
  var format = dateUtils.format,
    dateValidator = dateUtils.dateValidator,
    dateCalculator = dateUtils.dateCalculator,
    momentUtils = dateUtils.momentUtils;
  var isControlled = props.hasOwnProperty("value");
  var _useState = useState(new Map()),
    leftPanelRefList = _useState[0];
  var _React$useState = React.useState([]),
    inputRefList = _React$useState[0],
    setInputRefList = _React$useState[1];
  var _React$useState2 = React.useState([]),
    clickOutsideCallbackQueue = _React$useState2[0];
  var getStartDate = momentUtils.getStartDate,
    getEndDate = momentUtils.getEndDate,
    addDate = momentUtils.addDate,
    subtractDate = momentUtils.subtractDate;
  var _useRef = useRef({
      isCalled: false,
      previousValue: value || defaultValue
    }),
    current = _useRef.current;
  var onChangeCallBack = function onChangeCallBack(callbackValue, isValid) {
    if (!current.isCalled && !dateValidator.isSameDates(current.previousValue, callbackValue)) {
      if (callbackValue) {
        if (selectRange) {
          if (callbackValue instanceof Date) {
            callbackValue = [callbackValue];
          }
        }
      }
      if (callbackValue instanceof Array) {
        callbackValue = callbackValue.map(function (v, k) {
          if (k === 0) {
            return getStartDate(v, 'day');
          } else if (k === 1) {
            return getEndDate(v, 'day');
          }
        });
      }
      onChange && onChange(callbackValue, isValid);
      current.isCalled = true;
      setTimeout(function () {
        current.isCalled = false;
      }, 200);
      //need to clone array
      if (callbackValue instanceof Array) {
        current.previousValue = [].concat(callbackValue);
      } else {
        current.previousValue = callbackValue;
      }
    }
    ;
  };
  var initialValue = useMemo(function () {
    return props.value || props.defaultValue;
  }, [props.value, props.defaultValue]);
  var viewReducer = useMemo(function () {
    return viewReducerGen({
      minDetail: minDetail,
      maxDetail: maxDetail
    });
  }, [minDetail, maxDetail]);
  var _useReducer = useReducer(viewReducer, {
      view: 'month'
    }),
    viewStore = _useReducer[0],
    viewDispatch = _useReducer[1];
  var _useReducer2 = useReducer(valueReducerGen({
      onChange: onChangeCallBack,
      isControlled: isControlled,
      dateValidator: dateValidator,
      selectRange: selectRange,
      minDate: minDate,
      maxDate: maxDate,
      disabledDays: disabledDays,
      disabledDates: disabledDates,
      disabledRanges: disabledRanges,
      momentUtils: momentUtils,
      maxDetail: maxDetail,
      viewStore: viewStore
    }), {
      value: initialValue
    }),
    valueStore = _useReducer2[0],
    valueDispatch = _useReducer2[1];
  var _React$useState3 = React.useState(false),
    hasInputError = _React$useState3[0],
    setHasInputError = _React$useState3[1];
  var _dateCalculator$calcu = dateCalculator.calculateInitActiveDate(maxDetail, valueStore.value),
    activeStartDateLeft = _dateCalculator$calcu[0],
    activeStartDateRight = _dateCalculator$calcu[1];
  var _useReducer3 = useReducer(activeStartDateReducerGen({
      momentUtils: momentUtils,
      dateCalculator: dateCalculator,
      maxDetail: maxDetail,
      valueDispatch: valueDispatch,
      viewStore: viewStore,
      value: valueStore.value
    }), {
      activeStartDateLeft: activeStartDateLeft,
      activeStartDateRight: activeStartDateRight
    }),
    activeStartDateStore = _useReducer3[0],
    activeStartDateDispatch = _useReducer3[1];
  var _useReducer4 = useReducer(keyboardControlAreaReducerGen({
      activeStartDateStore: activeStartDateStore,
      viewStore: viewStore,
      disabled: disabled,
      isInDoubleCalendar: isInDoubleCalendar,
      leftPanelRefList: leftPanelRefList,
      dateValidator: dateValidator,
      dateCalculator: dateCalculator,
      inputRefList: inputRefList,
      momentUtils: momentUtils
    }), {
      keyboardControlArea: null,
      keyboardNavDate: null,
      keyboardNavDateRight: null
    }),
    keyboardControlStore = _useReducer4[0],
    keyboardControlAreaDispatch = _useReducer4[1];
  var _useReducer5 = useReducer(inputValueReducer({
      momentUtils: momentUtils,
      dateCalculator: dateCalculator
    }), {
      inputValue: ""
    }),
    inputValueStore = _useReducer5[0],
    inputValueDispatch = _useReducer5[1];
  var prevInputValue = usePrevious(inputValueStore.inputValue);
  useEffect(function () {
    if (isControlled) {
      valueDispatch({
        type: 'setValueByProps',
        payload: {
          value: props.value
        }
      });
    }
    ;
    var value = isControlled ? props.value : valueStore.value;
    var isValidByRule = dateValidator.isValidDateValueByRules({
      date: value,
      minDate: minDate,
      maxDate: maxDate,
      disabledDays: disabledDays,
      disabledDates: disabledDates,
      disabledRanges: disabledRanges
    });
    setHasInputError(!isValidByRule);
  }, [isControlled ? props.value : valueStore.value, maxDate, minDate, disabledDates, disabledDays, disabledRanges]);
  useUpdateEffect(function () {
    if (prevInputValue !== inputValueStore.inputValue) {
      if (isControlled && typeof props.value == "undefined" && !inputValueStore.inputValue) {
        return;
      }
      // custom single
      if (!selectRange && inputValueStore.inputValue.length > format.length) {
        onValueChange && onValueChange(inputValueStore.inputValue.substr(0, format.length));
      } else {
        onValueChange && onValueChange(inputValueStore.inputValue);
      }
    }
  }, [inputValueStore.inputValue]);

  // when value/ default value exists, 
  // do validation once
  useEffect(function () {
    var value = props.value || props.defaultValue;
    if (!value) return;
    var isValidByRule = dateValidator.isValidDateValueByRules({
      date: value,
      minDate: minDate,
      maxDate: maxDate,
      disabledDays: disabledDays,
      disabledDates: disabledDates,
      disabledRanges: disabledRanges,
      view: viewStore.view
    });
    setHasInputError(!isValidByRule);
  }, []);
  var _useState2 = useState(false),
    isUsingKeyboard = _useState2[0],
    setIsUsingKeyboard = _useState2[1];
  var mergedDisabledDatesIntervals = useMemo(function () {
    var internalDisabledRanges = disabledRanges || [];
    var internalDisabledDates = disabledDates || [];
    //8640000000000000 is the max/min value Date Object support;
    var maxDateInterval = maxDate ? [addDate(maxDate, 1, 'day'), new Date(8640000000000000)] : [];
    var minDateInterval = minDate ? [subtractDate(minDate, 1, 'day'), new Date(-8640000000000000)] : [];
    var disabledDatesIntervals = [].concat(internalDisabledDates.map(function (date) {
      return [date, date];
    }), internalDisabledRanges, [maxDateInterval, minDateInterval]);
    return dateCalculator.mergeDisabledDatesIntervals(disabledDatesIntervals, DetailToGranularityMap.get(maxDetail));
  }, [disabledDates, disabledRanges, maxDetail]);
  var uuid = useMemo(function () {
    return _uuid(10, 8);
  }, []);
  var _useReducer6 = useReducer(openStatusReducerGen(_extends({}, props, {
      format: format,
      keyboardControlAreaDispatch: keyboardControlAreaDispatch,
      activeStartDateDispatch: activeStartDateDispatch,
      viewDispatch: viewDispatch,
      dateValidator: dateValidator,
      valueDispatch: valueDispatch,
      inputValueDispatch: inputValueDispatch,
      dateCalculator: dateCalculator,
      mergedDisabledDatesIntervals: mergedDisabledDatesIntervals,
      setHasInputError: setHasInputError,
      maxDetail: maxDetail
    })), {
      isOpen: false
    }),
    openStatusStore = _useReducer6[0],
    openStatusDispatch = _useReducer6[1];

  // Expose custom functions or values using useImperativeHandle  
  React.useImperativeHandle(ref, function () {
    return {
      triggerOpen: triggerOpen,
      current: dateRef.current // Expose the DOM node directly
    };
  });

  var triggerOpen = function triggerOpen(isOpen) {
    if (isOpen) {
      openStatusDispatch({
        type: "openWithValidation",
        payload: {
          inputValue: inputValueStore.inputValue,
          selectRange: selectRange,
          isKeyboard: false,
          editForbidden: editForbidden,
          panelValue: value
        }
      });
    } else {
      openStatusDispatch({
        type: "closeWithValidation",
        payload: {
          inputValue: inputValueStore.inputValue,
          mode: mode,
          selectRange: selectRange,
          isKeyboard: false,
          editForbidden: editForbidden,
          panelValue: value
        }
      });
    }
  };
  var initialStore = _extends({}, props, {
    value: valueStore.value,
    valueDispatch: valueDispatch,
    openStatusStore: openStatusStore,
    openStatusDispatch: openStatusDispatch,
    activeStartDateStore: activeStartDateStore,
    activeStartDateDispatch: activeStartDateDispatch,
    keyboardControlStore: keyboardControlStore,
    keyboardControlAreaDispatch: keyboardControlAreaDispatch,
    viewStore: viewStore,
    viewDispatch: viewDispatch,
    dateValidator: dateValidator,
    dateCalculator: dateCalculator,
    momentUtils: momentUtils,
    inputValueStore: inputValueStore,
    inputValueDispatch: inputValueDispatch,
    hasInputError: hasInputError,
    inputRefList: inputRefList,
    setInputRefList: setInputRefList,
    leftPanelRefList: leftPanelRefList,
    mergedDisabledDatesIntervals: mergedDisabledDatesIntervals,
    minDetail: minDetail,
    maxDetail: maxDetail,
    minDate: minDate,
    maxDate: maxDate,
    disabledDays: disabledDays,
    disabledDates: disabledDates,
    disabledRanges: disabledRanges,
    showPanel: showPanel,
    uuid: uuid,
    hideTitleOnInput: hideTitleOnInput,
    isUsingKeyboard: isUsingKeyboard,
    setIsUsingKeyboard: setIsUsingKeyboard,
    clickOutsideCallbackQueue: clickOutsideCallbackQueue,
    format: format,
    editForbidden: editForbidden,
    datepickerAlwaysDown: datepickerAlwaysDown,
    datePanelHorizontalAlign: datePanelHorizontalAlign,
    useCustomValidation: useCustomValidation,
    error: error,
    customErrorNode: customErrorNode
  });
  var cn = ClassNames("a-calendar", className);
  return /*#__PURE__*/React.createElement(CalendarPickerContext.Provider, {
    value: initialStore
  }, /*#__PURE__*/React.createElement("div", {
    className: cn,
    style: style,
    id: id,
    ref: dateRef
  }, selectRange ? /*#__PURE__*/React.createElement(DateRangePicker, _extends({}, props, {
    minDetail: viewEnd,
    maxDetail: viewStart,
    format: format,
    value: valueStore.value,
    isInDoubleCalendar: isInDoubleCalendar,
    editable: editable,
    dateSeparator: dateSeparator,
    rangeConnecterFormat: rangeConnecterFormat
  })) : /*#__PURE__*/React.createElement(DateSinglePicker, _extends({}, props, {
    minDetail: viewEnd,
    maxDetail: viewStart,
    rangeConnecterFormat: rangeConnecterFormat,
    format: format,
    value: valueStore.value,
    isInDoubleCalendar: isInDoubleCalendar,
    editable: editable,
    dateSeparator: dateSeparator
  }))));
});
export { CalendarPicker };